import merge from 'lodash.merge';

import getContentGroup from '#shared/util/getContentGroup.js';
import forceHttps from '#shared/util/forceHttps.js';
import appError from '#shared/util/appError.js';
import json from '#shared/util/json.js';
import Price from '#shared/util/Price.js';

/**
 * Map of NLOK fulfillments to the checkout ones
 * @note Unmapped keys will be removed from the fulfillment array
 */
const licenseTypeMap = {
	WALLET_KEY: 'walletkey'
};
const knownLicenseTypes = Object.values(licenseTypeMap);

/**
 * Extract per-product values from pricing response object
 * @param {string} product Product foreign ID
 * @param {object} pricingResponse Object returned from pp/cartData schema
 * @returns {object}
 */
const getProductDataFromPricing = (product, pricingResponse) => {
	const entries = Object.entries(pricingResponse)
		.filter(([, value]) => Boolean((value || {})[product]))
		.map(([property, value]) => [property, (value || {})[product]]);
	return Object.fromEntries(entries);
};

/**
 * Get transformed license array
 * @param {Array} responseLicenseArray
 * @returns {Array}
 */
const getTransformedLicenseArray = (responseLicenseArray) => {
	if (Array.isArray(responseLicenseArray)) {
		return responseLicenseArray.map((license) => {
			return {
				contentType: licenseTypeMap[license.key] || license.key,
				content: license.value
			};
		}).filter((license) => {
			return knownLicenseTypes.includes(license.contentType);
		});
	} else {
		return null;
	}
};

/**
 * Add fulfillment details to each item
 * @param {object} dependencies
 * @param {object} dependencies.cart
 * @param {object} dependencies.item
 * @param {object} dependencies.data
 */
const addFulfillmentDetails = ({ cart, item, data }) => {
	if (cart.statusMessage === 'SUCCESS' && (item.status === 'FULFILLED' || item.status === 'CLOSED')) {
		const licenseArray = getTransformedLicenseArray(item.fulfillment || []);
		data.fulfillment = {
			state: item.status,
			isDelivered: Array.isArray(licenseArray) && licenseArray.length > 0,
			license: licenseArray
		};
		data.dimension37 = data.fulfillment.isDelivered ? 'Yes' : 'No';
		if (!data.fulfillment.isDelivered) {
			appError('Fulfillment pending', {
				code: 'APP_SHARED_SCHEMA',
				origin: 'product',
				detail: {
					orderId: cart.result.cartGuid,
					aswparam: cart.aswparam ? cart.aswparam.string : null, // @todo GEN - Missing value
					product: item.foreignId || item.externalSkuCode,
					fulfillment: json.stringify(data.fulfillment || {})
				}
			}, 'warn');
		}
	} else {
		data.fulfillment = {
			state: item.status,
			isDelivered: false
		};
	}
};

/**
 * Product schema
 * @param {object} data
 * @param {object} data.cart Object with raw cart data from eStore API
 * @param {object} data.payment Object returned from payment schema
 * @param {number} data.index Index of the item in the array
 * @param {object} data.item Item from eStore API
 * @param {object} data.sessionStore Session store instance
 * @param {string} [data.offerType] crossSell, upsell or undefined
 * @returns {object}
 */
export const productSchema = ({ cart, payment, index, item, sessionStore, offerType }) => {
	const productId = String(item.foreignId || item.externalSkuCode);
	const ppProductId = [productId, item.quantity ?? null, item.price?.finalPrice ?? null].filter(Boolean).join('-');
	const customIDs = sessionStore.store.cartParameters.customIDs;
	const pricingResponse = sessionStore.store.pricingResponse || {};
	const { productInfo, productParameters, price, futurePrice, conversionPrice, discount, variations, installments } = merge(getProductDataFromPricing(productId, pricingResponse), getProductDataFromPricing(ppProductId, pricingResponse));
	const categories = productInfo.categories;
	const contentGroup = getContentGroup(categories);
	const data = {
		installments,
		variations,
		id: productId,
		sku: productId,
		name: productInfo.name,
		internalId: productInfo.internalId,
		category: {
			array: categories,
			string: categories.join(', ')
		},
		contentGroup,
		quantity: item.quantity || 1,
		position: index + 1,
		coupon: discount && discount.coupon || 'noDiscount',
		guid: item.guid,
		disclaimer: productParameters.disclaimer,
		displayPhoneNumber: productParameters.displayPhoneNumber,
		isTrial: productParameters.isTrial,
		isBusiness: contentGroup === 'Business',
		isBrowser: categories.includes('Browser'),
		isAutomaticRenewal: productParameters.isAutomatic,
		isManualRenewal: !productParameters.isAutomatic,
		trialPeriod: productParameters.trialPeriod,
		headline: productInfo.offerHeadline,
		description: productInfo.description,
		automaticRenewalNote: productInfo.automaticRenewalNote,
		manualRenewalNote: productInfo.manualRenewalNote,
		note: productInfo.automaticRenewalNote,
		alphaIds: productInfo.alphaId || [],
		image: {
			thumbnail: forceHttps(productInfo.image)
		},
		showMonthlyPrice: customIDs.includes('PPMONTH'),
		price,
		priceDisclaimer: productParameters.priceDisclaimer,
		futurePrice,
		conversionPrice,
		foreignId: productId,
		// Determine if VS/quantity field should be visible
		showQuantity: !productParameters.hideQuantity,
		minQuantityLimit: productParameters.minQuantityLimit || 1,
		maxQuantityLimit: productParameters.maxQuantityLimit || 100,
		usageBase: productParameters.usageValidityValue || 100,
		quantityAs: productParameters.usageValidityIsDisplayed ? 'usage' : 'devices',
		showVariations: (customIDs.includes('VSON') || productParameters.lengthValidityIsDisplayed) && variations && variations.length > 1,
		// Add selected variation data
		variant: productParameters.isAutomatic ? 'AR' : 'MR',
		devices: productParameters.seatsValidityValue,
		monthsValidity: productParameters.lengthValidityValue,
		yearsValidity: productParameters.lengthValidityValue / 12
	};
	// @todo GEN - Temporary hack to get formatted order pricing
	const priceFormatter = new Price(data.price.street.formatted);
	data.price.tax = priceFormatter.formatPrice(item.tax && item.tax.taxAmount || 0);
	// Add fulfillment details to each item if not an offer product
	if (!offerType) {
		const fulfilledItem = (cart.orderLines || []).find((item) => item.mappedSku === productId) || {};
		const itemWithFulfillment = merge({}, item, fulfilledItem);
		addFulfillmentDetails({
			cart,
			data,
			item: itemWithFulfillment
		});
	}
	// Update for manual renewal products
	if (payment?.isManualRenewal || data.isManualRenewal) {
		// Specify dimenstion37 for manual renewal methods
		data.dimension37 = 'Offline';
		// Show product note for manual renewal
		data.note = data.manualRenewalNote;
	}
	// Once the fulfillment is delivered, replace OFI_KEY with delivered key value
	if (Array.isArray(productParameters.thankYouPageText) && data.fulfillment && data.fulfillment.isDelivered) {
		const walletKey = data.fulfillment.license.find((item) => ['walletkey', 'activationkey'].includes(item.contentType)).content;
		const thankYouPageText = productParameters.thankYouPageText;
		data.thankYouPageText = walletKey ? JSON.parse(JSON.stringify(thankYouPageText).replace(/{?OFI_KEY}?/g, walletKey)) : thankYouPageText;
	}
	// Return transformed data
	return data;
};
